import { useCallback } from 'react'
import { OnChangeValue } from 'react-select/dist/declarations/src/types'
import { Box, Flex, Icon, IconButton, Text, useTheme } from '@chakra-ui/react'
import { AsyncCreatableSelect } from 'chakra-react-select'
import { Trash2, X } from 'emotion-icons/feather'
import { AlgoliaTag, AllTagTypes } from 'entities/tag'
import { transformAlgoliaResponse } from 'lib/algolia'
import { useAlgoliaIndex } from 'hooks/useAlgoliaIndex'
import useQuery from 'hooks/useQuery'
import { useVertical } from 'hooks/useVerticalData'
import { QUERY_ASSOCIATED_TAGS } from 'containers/admin/vertical-tags-association/queries'
import {
    ClearIndicator,
    DropdownIndicator,
    getChakraStyles,
    Group,
    Input,
    LoadingIndicator,
    Option as OptionComponent,
} from 'containers/jobs-board/components/SearchInput/components'
import {
    CountryCodeToCountryName,
    createRemunerationCurrencyOption,
    createRoleTypeOption,
    createLocationCityOption,
    createLocationCountryCodeOption,
    createLocationRegionOption,
    createRelocationOption,
    createRemoteOption,
    createRemunerationMinOption,
    createSearchOption,
    createTagOption,
    createRemunerationTimePeriodOption,
    createCompanyPackagesOption,
    createManagerOption,
    createPublishedOption,
    isRemunerationCurrencyOption,
    isRoleTypeOption,
    isLocationCityOption,
    isLocationCountryCodeOption,
    isLocationRegionOption,
    isRelocationOption,
    isRemoteOption,
    isRemunerationMinOption,
    isSearchOption,
    isTagOption,
    isRemunerationTimePeriodOption,
    isCompanyPackagesOption,
    isManagerOption,
    isPublishedOption,
    SelectOption,
} from 'containers/jobs-board/components/options'
import { useJobsSearchContext } from 'containers/jobs-board/context/JobsSearchContext'
import { suggestedSearchTerms } from 'containers/jobs-board/data'
import { Params } from 'containers/jobs-board/hooks/useParams'

// -----------------------------------------------------

const id = 'search-input'
const ASSOCIATED_TAGS_LIMIT = 13

const useTechStack = () => {
    const vertical = useVertical()
    const [{ data }] = useQuery({
        query: QUERY_ASSOCIATED_TAGS,
        variables: { vertical: vertical, limit: ASSOCIATED_TAGS_LIMIT },
    })

    return {
        techStack: data?.associated_tags?.results,
    }
}

const componentsDesktop = {
    DropdownIndicator,
    ClearIndicator,
    Group,
    Option: OptionComponent,
    LoadingIndicator,
}

const componentsMobile = {
    DropdownIndicator,
    ClearIndicator,
    Group,
    Option: OptionComponent,
    Input,
    LoadingIndicator,
}

const getSelectedOptionsFromParams = (
    filters: Params,
    countryCodeToCountryName: CountryCodeToCountryName,
) => {
    const searchOptions = filters.search?.map(s => createSearchOption(s, 'Param')) || []
    const tagOptions = filters.tags?.map(createTagOption) || []
    const locationCityOptions = filters.locationCity?.map(createLocationCityOption) || []
    const locationRegionOptions =
        filters.locationRegion?.map(createLocationRegionOption) || []
    const locationCountryCodeOptions =
        filters.locationCountryCode
            ?.filter(code => Boolean(countryCodeToCountryName[code]))
            .map(code =>
                createLocationCountryCodeOption(countryCodeToCountryName[code], code),
            ) || []
    const remoteOptions = filters.remote ? [createRemoteOption()] : []
    const relocationOptions = filters.sponsorshipOffered ? [createRelocationOption()] : []
    const roleTypeOptions = filters.roleTypes?.map(createRoleTypeOption) || []
    const remunerationMinOptions = filters.remunerationMin
        ? [createRemunerationMinOption(filters.remunerationMin)]
        : []
    const remunerationCurrencyOptions = filters.remunerationCurrency
        ? [createRemunerationCurrencyOption(filters.remunerationCurrency)]
        : []
    const remunerationTimePeriodOptions = filters.remunerationTimePeriod
        ? [createRemunerationTimePeriodOption(filters.remunerationTimePeriod)]
        : []
    // Admin-specific
    const companyPackagesOptions =
        filters.companyPackages?.map(createCompanyPackagesOption) || []
    const managerOptions = filters.manager ? [createManagerOption()] : []
    const publishedOptions = filters.published ? [createPublishedOption()] : []

    return [
        ...searchOptions,
        ...tagOptions,
        ...locationCityOptions,
        ...locationRegionOptions,
        ...locationCountryCodeOptions,
        ...remoteOptions,
        ...relocationOptions,
        ...roleTypeOptions,
        ...remunerationMinOptions,
        ...remunerationCurrencyOptions,
        ...remunerationTimePeriodOptions,
        ...companyPackagesOptions,
        ...managerOptions,
        ...publishedOptions,
    ]
}

export const SearchInput = ({
    loading,
    countryCodeToCountryName,
}: {
    loading: boolean
    countryCodeToCountryName: CountryCodeToCountryName
}) => {
    const { techStack } = useTechStack()

    const { filters, updateFilters } = useJobsSearchContext()

    const defaultOptions = [
        {
            label: 'Suggested search terms:',
            options: suggestedSearchTerms.map(s => createSearchOption(s, 'Preset')),
        },
        {
            label: 'Suggested tech stack:',
            options: techStack ? techStack.map(ts => ts.label).map(createTagOption) : [],
        },
    ]

    const queryOptions = getSelectedOptionsFromParams(filters, countryCodeToCountryName)

    // Algolia tags search
    const indexTags = useAlgoliaIndex('tags')

    // options/tags to display when searching
    const promiseOptions = async (searchQuery: string) => {
        const res = await indexTags.search<AlgoliaTag>(searchQuery, {
            filters: `type:${AllTagTypes.tech}`,
        })

        return [
            {
                label: 'Suggested:',
                options: transformAlgoliaResponse<AlgoliaTag>(res)?.map(resp =>
                    createTagOption(resp.label),
                ),
            },
        ]
    }

    const onChange = useCallback(
        (optionValues: OnChangeValue<SelectOption, true>) => {
            const search = optionValues.filter(isSearchOption).map(({ value }) => value)
            const tags = optionValues.filter(isTagOption).map(({ value }) => value)
            const roleTypes = optionValues.filter(isRoleTypeOption).map(({ value }) => value)
            const locationCity = optionValues
                .filter(isLocationCityOption)
                .map(({ value }) => value)
            const locationRegion = optionValues
                .filter(isLocationRegionOption)
                .map(({ value }) => value)
            const locationCountryCode = optionValues
                .filter(isLocationCountryCodeOption)
                .map(({ value }) => value)
            const remote = optionValues.some(isRemoteOption) ? true : undefined
            const sponsorshipOffered = optionValues.some(isRelocationOption)
                ? true
                : undefined
            const remunerationMin = optionValues.filter(isRemunerationMinOption)[0]?.value
            const remunerationCurrency = optionValues.filter(
                isRemunerationCurrencyOption,
            )[0]?.value
            const remunerationTimePeriod = optionValues.filter(
                isRemunerationTimePeriodOption,
            )[0]?.value
            const companyPackages = optionValues
                .filter(isCompanyPackagesOption)
                .map(({ value }) => value)
            const manager = optionValues.filter(isManagerOption)[0]?.value
            const published = optionValues.some(isPublishedOption) ? true : undefined

            updateFilters({
                search,
                tags,
                locationCity,
                locationRegion,
                locationCountryCode,
                remote,
                sponsorshipOffered,
                roleTypes,
                remunerationMin,
                remunerationCurrency,
                remunerationTimePeriod,
                companyPackages,
                manager,
                published,
            })
        },
        [updateFilters],
    )

    const theme = useTheme()
    const chakraStyles = getChakraStyles({
        menuProps: {
            zIndex: theme.zIndices.popover,
        },
    })

    return (
        <>
            <Box display={{ base: 'none', md: 'block' }}>
                <AsyncCreatableSelect<SelectOption, true>
                    chakraStyles={chakraStyles}
                    placeholder="Search tech stack, role titles and more"
                    components={componentsDesktop}
                    isLoading={loading}
                    isMulti
                    isSearchable
                    inputId={id}
                    instanceId={id}
                    options={defaultOptions}
                    loadOptions={promiseOptions}
                    defaultOptions={defaultOptions}
                    createOptionPosition="first"
                    formatCreateLabel={term => `Search for "${term}"`}
                    value={queryOptions}
                    onChange={onChange}
                    autoFocus={true}
                    tabSelectsValue={false}
                    colorScheme="primary"
                    size="lg"
                />
            </Box>

            <Box display={{ base: 'block', md: 'none' }}>
                <AsyncCreatableSelect<SelectOption, true>
                    chakraStyles={chakraStyles}
                    placeholder="Search jobs"
                    components={componentsMobile}
                    isLoading={loading}
                    isMulti
                    isSearchable
                    inputId={`${id}-mobile`}
                    instanceId={`${id}-mobile`}
                    options={defaultOptions}
                    loadOptions={promiseOptions}
                    defaultOptions={defaultOptions}
                    createOptionPosition="first"
                    formatCreateLabel={term => `Search for "${term}"`}
                    value={queryOptions}
                    onChange={onChange}
                    autoFocus={true}
                />
            </Box>
        </>
    )
}

export const SearchInputTagsForMobile = ({
    countryCodeToCountryName,
}: {
    countryCodeToCountryName: CountryCodeToCountryName
}) => {
    const { filters, updateFilters, resetFilters } = useJobsSearchContext()

    const options = getSelectedOptionsFromParams(filters, countryCodeToCountryName)

    const removeItem = useCallback(
        (params: Params, selectedQueryValue: SelectOption) => {
            const {
                search,
                tags,
                locationCity,
                locationRegion,
                locationCountryCode,
                remote,
                sponsorshipOffered,
                roleTypes,
                remunerationMin,
                remunerationCurrency,
                remunerationTimePeriod,
                companyPackages,
                manager,
                published,
            } = params

            const filteredSearch = search?.filter(
                query => query !== selectedQueryValue?.value,
            )
            const filteredTags = isTagOption(selectedQueryValue)
                ? tags?.filter(query => query !== selectedQueryValue?.value)
                : tags
            const filteredLocationCity = isLocationCityOption(selectedQueryValue)
                ? locationCity?.filter(query => query !== selectedQueryValue?.value)
                : locationCity
            const filteredLocationRegion = isLocationRegionOption(selectedQueryValue)
                ? locationRegion?.filter(query => query !== selectedQueryValue?.value)
                : locationRegion
            const filteredLocationCountryCode = isLocationCountryCodeOption(
                selectedQueryValue,
            )
                ? locationCountryCode?.filter(
                      query => query !== selectedQueryValue?.value,
                  )
                : locationCountryCode
            const filteredRemote = isRemoteOption(selectedQueryValue) ? undefined : remote
            const filteredRoleTypes = isRoleTypeOption(selectedQueryValue)
                ? roleTypes?.filter(query => query !== selectedQueryValue?.value)
                : roleTypes
            const filteredSponsorshipOffered = isRelocationOption(selectedQueryValue)
                ? undefined
                : sponsorshipOffered
            const filteredRemunerationMin = isRemunerationMinOption(selectedQueryValue)
                ? undefined
                : remunerationMin
            const filteredRemunerationCurrency = isRemunerationCurrencyOption(
                selectedQueryValue,
            )
                ? undefined
                : remunerationCurrency
            const filteredRemunerationTimePeriod = isRemunerationTimePeriodOption(
                selectedQueryValue,
            )
                ? undefined
                : remunerationTimePeriod
            const filteredCompanyPackages = isCompanyPackagesOption(selectedQueryValue)
                ? companyPackages?.filter(query => query !== selectedQueryValue?.value)
                : companyPackages
            const filteredManager = isManagerOption(selectedQueryValue)
                ? undefined
                : manager
            const filteredPublished = isPublishedOption(selectedQueryValue)
                ? undefined
                : published

            updateFilters({
                search: filteredSearch,
                tags: filteredTags,
                locationCity: filteredLocationCity,
                locationRegion: filteredLocationRegion,
                locationCountryCode: filteredLocationCountryCode,
                remote: filteredRemote,
                sponsorshipOffered: filteredSponsorshipOffered,
                roleTypes: filteredRoleTypes,
                remunerationMin: filteredRemunerationMin,
                remunerationCurrency: filteredRemunerationCurrency,
                remunerationTimePeriod: filteredRemunerationTimePeriod,
                companyPackages: filteredCompanyPackages,
                manager: filteredManager,
                published: filteredPublished,
            })
        },
        [updateFilters],
    )

    if (options.length === 0) {
        return null
    }

    return (
        <Flex
            display={{ base: 'flex', md: 'none' }}
            alignItems="center"
            justifyContent="space-between"
            w="full"
            mt="14px"
        >
            <Flex flexGrow="1" flexWrap="wrap" gap="4px">
                {options?.map(selectedQueryValue => {
                    if (!selectedQueryValue) return

                    const handleRemoveItem = () => {
                        removeItem(filters, selectedQueryValue as SelectOption)
                    }

                    return (
                        <Flex
                            key={selectedQueryValue?.value}
                            gap="5px"
                            py="5px"
                            pl="10px"
                            pr="6px"
                            rounded="4px"
                            bgColor="secondary_wh.300"
                            color="white"
                            h="30px"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Text
                                color="white"
                                textStyle="functionalButtonsSmall"
                                fontWeight="medium"
                            >
                                {selectedQueryValue?.label}
                            </Text>

                            <IconButton
                                onClick={handleRemoveItem}
                                aria-label={`Remove ${selectedQueryValue?.value}`}
                                variant="unstyled"
                                icon={
                                    <Icon
                                        as={X}
                                        stroke="currentColor"
                                        strokeWidth="1.5px"
                                        w="16px"
                                        h="16px"
                                    />
                                }
                                minW="unset"
                            />
                        </Flex>
                    )
                })}
            </Flex>

            <IconButton
                ml="4px"
                aria-label="Clear search filters"
                variant="unstyled"
                icon={
                    <Icon
                        order={1}
                        fontSize="29px"
                        strokeWidth="1.5px"
                        as={Trash2}
                        color="text.secondary"
                    />
                }
                onClick={resetFilters}
            />
        </Flex>
    )
}
